import { company } from "@purlink/shared/company";
import { createFileRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { PublicLayout } from "~/components/public/public-layout";
import { Card } from "~/components/ui/card";

export const Route = createFileRoute("/privacy")({
  component: PrivacyPage,
});

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-h2 text-fg">{title}</h2>
      <div className="flex flex-col gap-2 text-body text-fg-secondary [&_li]:ml-5 [&_li]:list-disc">
        {children}
      </div>
    </section>
  );
}

/**
 * Privacy policy under the Australian Privacy Act 1988 (docs §26). Content
 * lives in code, not the database. Have it legally reviewed before launch.
 */
function PrivacyPage() {
  return (
    <PublicLayout narrow>
      <Card className="flex flex-col gap-7 p-6 sm:p-10">
        <div className="flex flex-col gap-2">
          <h1 className="text-h1 text-fg">Privacy policy</h1>
          <p className="text-small text-fg-muted">Last updated: October 2026</p>
        </div>

        <Section title="Who we are">
          <p>
            {company.tradingName} is a product of {company.legalName} ("we"), an Australian IT
            company. We're responsible for the personal information you give us, and we handle it
            under the Australian Privacy Act 1988 and the Australian Privacy Principles.{" "}
            {company.registration}. Registered office: {company.registeredOffice}.
          </p>
        </Section>

        <Section title="What we collect">
          <p>When you send an enquiry we collect:</p>
          <ul>
            <li>your name, email address and phone number;</li>
            <li>
              if you choose to give them: your company, the service you're interested in and your
              message;
            </li>
            <li>whether you'd like to receive marketing emails;</li>
            <li>
              how you found us: campaign tags and advertising click identifiers in the link you
              followed, the page you landed on and the referring website;
            </li>
            <li>technical details: your IP address, approximate country and browser type.</li>
          </ul>
        </Section>

        <Section title="Why we use it">
          <ul>
            <li>To respond to your enquiry and discuss our services.</li>
            <li>To understand which of our marketing works. We use this in aggregate.</li>
            <li>
              To measure and improve our Facebook and Instagram advertising. When you send an
              enquiry, we share a hashed (scrambled) copy of your email address and phone number
              with Meta, together with your IP address, browser type, approximate country and any
              Meta ad click identifier, so Meta can tell us whether the enquiry came from one of our
              ads. Our enquiry and privacy pages also use the Meta Pixel, which tells Meta that your
              browser visited them, so we can show our ads to people likely to be interested and
              measure how they perform. You can control the ads Meta shows you in your{" "}
              <a
                className="font-medium text-primary hover:underline"
                href="https://www.facebook.com/adpreferences"
                target="_blank"
                rel="noreferrer"
              >
                Facebook ad preferences
              </a>
              .
            </li>
            <li>To protect the form from spam and abuse.</li>
            <li>
              To send you marketing emails, only if you ticked the box to say you'd like them
              (consent). You can withdraw consent at any time.
            </li>
          </ul>
        </Section>

        <Section title="Who we share it with">
          <p>We don't sell your information. Trusted providers process it on our behalf:</p>
          <ul>
            <li>Resend, to send emails (United States).</li>
            <li>Cloudflare, for website security and spam protection.</li>
            <li>
              Meta Platforms Ireland Ltd, to measure and target our advertising (see above). Meta
              uses this information under its own terms, and may transfer it to the United States.
            </li>
            <li>
              Google LLC (Google Analytics), to measure visits to our enquiry and privacy pages and
              how well our campaigns work (United States). It doesn't receive your name, email or
              phone number.
            </li>
            <li>Our hosting provider, which stores the data.</li>
          </ul>
          <p>
            Some of these providers store or process information outside Australia, mainly in the
            United States and the European Union. We take reasonable steps to make sure they handle
            it in line with the Australian Privacy Principles.
          </p>
        </Section>

        <Section title="How long we keep it">
          <p>
            Our retention periods are still being finalised. Until they are, we keep enquiry
            information for as long as it's needed to manage our relationship with you.
          </p>
        </Section>

        <Section title="Cookies and local storage">
          <p>
            The enquiry form stores a small record of the campaign that first brought you to us in
            your browser's local storage for up to 90 days, so we can understand which marketing
            works. It also loads Cloudflare's security check. Our enquiry and privacy pages use
            Google Analytics, which sets cookies (named _ga and _ga_…) to count visits and see which
            campaigns bring people to us, and the Meta Pixel, which sets a cookie (named _fbp) so
            Meta can recognise your browser for our advertising. Staff sign-in uses a strictly
            necessary session cookie.
          </p>
        </Section>

        <Section title="Your rights">
          <p>
            You can ask to access or correct your information, ask us to delete it, and withdraw
            your consent to marketing at any time. If you live outside Australia, your local law may
            give you further rights. Email{" "}
            <a
              className="font-medium text-primary hover:underline"
              href={`mailto:${company.privacyEmail}`}
            >
              {company.privacyEmail}
            </a>
            .
          </p>
          <p>
            If you're not happy with how we've handled a request or complaint, you can contact the
            Office of the Australian Information Commissioner (
            <a className="font-medium text-primary hover:underline" href="https://www.oaic.gov.au">
              oaic.gov.au
            </a>
            ).
          </p>
        </Section>

        <Section title="Contact us">
          <p>
            {company.legalName}, {company.registeredOffice}. Email{" "}
            <a
              className="font-medium text-primary hover:underline"
              href={`mailto:${company.contactEmail}`}
            >
              {company.contactEmail}
            </a>{" "}
            or call {company.contactPhone}.
          </p>
        </Section>
      </Card>
    </PublicLayout>
  );
}
